// One attempt, with the evidence behind its verdict: replay with the measured
// feet and targets overlaid, the exact landing and hold frames, a top-down
// view, the movement plot — and a way to say whether the app got it right.

import { useEffect, useMemo, useRef, useState } from 'react';
import { buildFeedback, ISSUE_TEXT } from '../../core/feedback';
import { paramsFor } from '../../core/params';
import { deserializeFrames, type PoseFrame } from '../../core/pose';
import { TRUTH_TEXT, type TruthLabel } from '../../core/stats';
import { db, deleteAttempt, type AttemptRecord, type CalibrationRecord, type ModelVariant, type SessionRecord, type Still } from '../../engine/db';
import { reanalyseAttempt } from '../../engine/offline';
import { EXPERIMENT_SCENARIOS } from '../../engine/practice';
import { navigate, useApp, useBlobUrl } from '../app-state';
import { BoardView } from '../components/BoardView';
import { EvidencePlot } from '../components/charts';
import { fmtSec, Notice, OutcomeBadge, PageHead, Segmented } from '../components/bits';
import { Icon } from '../components/Icon';
import { Stage } from '../components/Stage';
import { drawOverlay } from '../draw/overlay';

export function Attempt({ id }: { id: string }) {
  const app = useApp();
  const [a, setA] = useState<AttemptRecord | null | undefined>(undefined);
  const [cal, setCal] = useState<CalibrationRecord | null>(null);
  const [session, setSession] = useState<SessionRecord | null>(null);
  const [siblings, setSiblings] = useState<string[]>([]);

  useEffect(() => {
    (async () => {
      const rec = await db.getAttempt(id);
      setA(rec ?? null);
      if (!rec) return;
      setCal((await db.getCalibration(rec.calibrationId)) ?? null);
      setSession((await db.getSession(rec.sessionId)) ?? null);
      setSiblings((await db.attemptsForSession(rec.sessionId)).map((x) => x.id));
    })();
  }, [id]);

  const frames = useMemo(() => (a ? deserializeFrames(a.frames) : []), [a]);
  if (a === undefined) return <div className="page"><div className="spinner" /></div>;
  if (a === null || !cal) {
    return (
      <div className="page">
        <PageHead title="Attempt" back={() => history.back()} />
        <p className="muted">{a === null ? 'This attempt no longer exists.' : 'Loading…'}</p>
      </div>
    );
  }
  const P = session?.params ?? paramsFor({ strictness: app.settings.strictness }, cal.reference.noise);
  const fb = buildFeedback(a.analysis, P);
  const tol = { along: P.tolAlong, across: P.tolAcross };
  const pos = siblings.indexOf(a.id);

  const update = async (patch: Partial<AttemptRecord>) => {
    const next = { ...a, ...patch };
    await db.putAttempt(next);
    setA(next);
  };

  const remove = async () => {
    if (!confirm('Delete this attempt?')) return;
    await deleteAttempt(a);
    if (session) await db.putSession({ ...session, attemptIds: session.attemptIds.filter((x) => x !== a.id) });
    navigate(`session/${a.sessionId}`, { replace: true });
  };

  return (
    <div className="page stack lg">
      <PageHead title={`Attempt ${a.index + 1}`} back={() => navigate(`session/${a.sessionId}`)}>
        <button className="icon-btn" disabled={pos <= 0} onClick={() => navigate(`attempt/${siblings[pos - 1]}`, { replace: true })} aria-label="Previous attempt">
          <Icon name="back" />
        </button>
        <button className="icon-btn" disabled={pos < 0 || pos >= siblings.length - 1} onClick={() => navigate(`attempt/${siblings[pos + 1]}`, { replace: true })} aria-label="Next attempt">
          <Icon name="chevron" />
        </button>
      </PageHead>

      <div className="stack sm">
        <OutcomeBadge outcome={a.analysis.outcome} large />
        <p style={{ fontSize: 18 }}>{fb.spoken}</p>
        {a.scenario && (
          <p className="small faint">
            You were asked: {EXPERIMENT_SCENARIOS.find((x) => x.key === a.scenario)?.instruction ?? a.scenario}
          </p>
        )}
      </div>

      {a.videoKey ? (
        <Replay attempt={a} frames={frames} cal={cal} tol={tol} />
      ) : (
        <Notice>The clip for this attempt has been removed to save space; the measurements and stills are kept.</Notice>
      )}

      <div className="tiles">
        <StillCard title="Landing" still={a.stills.landing} cal={cal} tol={tol} />
        <StillCard title="Hold" still={a.stills.settled} cal={cal} tol={tol} />
      </div>

      <div className="card stack">
        <h3>On the board</h3>
        <BoardView
          reference={cal.reference}
          stance={cal.stance}
          tol={tol}
          tape={cal.tape}
          initial={a.analysis.initial}
          settled={a.analysis.settled}
          adjustments={a.analysis.adjustments}
        />
        <ul className="small muted" style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>
          {fb.lines.map((l) => (
            <li key={l}>{l}</li>
          ))}
          {a.analysis.issues.filter((i) => ISSUE_TEXT[i]).map((i) => (
            <li key={i}>{ISSUE_TEXT[i]}</li>
          ))}
        </ul>
      </div>

      <div className="card stack">
        <div>
          <h3>Movement</h3>
          <p className="small muted">Times are from the “go” beep. Shaded: the landing window and the hold.</p>
        </div>
        <EvidencePlot analysis={a.analysis} reference={cal.reference} cueTime={a.cueTime} />
        <p className="tiny faint">
          Pop-up time {fmtSec(a.analysis.timing.popupMs)} · reaction {fmtSec(a.analysis.timing.reactionMs)} · person visible {Math.round(a.analysis.coverage * 100)}% of the movement · {a.source === 'live' ? `analysed live (${a.model})` : 'analysed frame by frame'}
        </p>
      </div>

      <LabelCard attempt={a} onChange={update} />

      {a.videoKey && <ModelCompare attempt={a} cal={cal} onUpdate={setA} />}

      <button className="btn danger" onClick={remove}>
        <Icon name="trash" size={18} /> Delete attempt
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------

function nearestFrame(frames: PoseFrame[], t: number): PoseFrame | null {
  if (!frames.length) return null;
  let lo = 0;
  let hi = frames.length - 1;
  while (lo < hi) {
    const m = (lo + hi) >> 1;
    if (frames[m].t < t) lo = m + 1;
    else hi = m;
  }
  const c = frames[lo];
  const p = frames[Math.max(0, lo - 1)];
  const best = Math.abs(p.t - t) < Math.abs(c.t - t) ? p : c;
  return Math.abs(best.t - t) < 120 ? best : null;
}

function Replay({ attempt: a, frames, cal, tol }: { attempt: AttemptRecord; frames: PoseFrame[]; cal: CalibrationRecord; tol: { along: number; across: number } }) {
  const url = useBlobUrl(a.videoKey);
  const [clipBlob, setClipBlob] = useState<Blob | null>(null);
  useEffect(() => {
    let live = true;
    if (a.videoKey) void db.getBlob(a.videoKey).then((b) => live && setClipBlob(b));
    return () => {
      live = false;
    };
  }, [a.videoKey]);
  const video = useMemo(() => {
    const v = document.createElement('video');
    v.playsInline = true;
    v.muted = true;
    v.setAttribute('playsinline', '');
    v.preload = 'auto';
    return v;
  }, []);
  const [rate, setRate] = useState(0.5);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [overlay, setOverlay] = useState(true);
  const overlayRef = useRef(overlay);
  overlayRef.current = overlay;

  useEffect(() => {
    if (!url) return;
    video.src = url;
    const onMeta = () => setDuration(Number.isFinite(video.duration) ? video.duration : (frames[frames.length - 1]?.t ?? 0) / 1000);
    const onTime = () => setTime(video.currentTime);
    const onEnd = () => setPlaying(false);
    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('ended', onEnd);
    return () => {
      video.removeEventListener('loadedmetadata', onMeta);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('ended', onEnd);
      video.pause();
    };
  }, [url, video, frames]);

  useEffect(() => {
    video.playbackRate = rate;
  }, [rate, video]);

  const toggle = () => {
    if (video.paused) {
      if (video.ended) video.currentTime = 0;
      void video.play();
      setPlaying(true);
    } else {
      video.pause();
      setPlaying(false);
    }
  };

  const ev = a.analysis.events;
  const marks = [
    { t: ev.landing, cls: 'land' },
    { t: ev.holdStart, cls: 'hold' },
  ].filter((m) => m.t !== undefined && duration > 0);

  return (
    <div className="stack sm">
      <div className="video-wrap">
        <Stage
          className="fill"
          view={video}
          size={() => ({ w: video.videoWidth || a.width, h: video.videoHeight || a.height })}
          draw={(ctx, r, dpr) => {
            if (!overlayRef.current) return;
            const f = nearestFrame(frames, video.currentTime * 1000 + a.videoOffsetMs);
            drawOverlay(ctx, r, { mapping: cal.mapping, tape: cal.tape, reference: cal.reference, tol, lm: f?.lm ?? null, stance: cal.stance }, dpr);
          }}
        />
      </div>
      <div className="scrubber">
        <input
          type="range"
          min={0}
          max={duration || 1}
          step={0.01}
          value={time}
          aria-label="Position"
          onChange={(e) => {
            video.currentTime = +e.target.value;
            setTime(+e.target.value);
          }}
        />
        <div className="marks">
          {marks.map((m) => (
            <i key={m.cls} className={m.cls} style={{ left: `${(((m.t! - a.videoOffsetMs) / 1000) / duration) * 100}%` }} />
          ))}
        </div>
      </div>
      <div className="row wrap">
        <button className="btn small" onClick={toggle}>
          <Icon name={playing ? 'pause' : 'play'} size={18} /> {playing ? 'Pause' : 'Play'}
        </button>
        <Segmented label="Speed" value={rate} onChange={setRate} options={[{ value: 0.25, label: '¼×' }, { value: 0.5, label: '½×' }, { value: 1, label: '1×' }]} />
        <label className="row small muted" style={{ gap: 6 }}>
          <input type="checkbox" checked={overlay} onChange={(e) => setOverlay(e.target.checked)} /> Overlay
        </label>
        {ev.landing !== undefined && (
          <button className="btn small ghost" onClick={() => (video.currentTime = Math.max(0, (ev.landing! - a.videoOffsetMs) / 1000))}>
            Jump to landing
          </button>
        )}
        <button className="btn small ghost" disabled={!clipBlob} onClick={() => shareClip(a, clipBlob)} aria-label="Share clip">
          <Icon name="share" size={18} /> Share
        </button>
      </div>
    </div>
  );
}

/**
 * Hands the clip to the share sheet (e.g. to send to a coach), or downloads
 * it. Called synchronously from the tap with a pre-loaded blob: iOS refuses
 * share() once an await has broken the user gesture.
 */
function shareClip(a: AttemptRecord, blob: Blob | null) {
  if (!blob) return;
  const ext = (a.videoMime ?? blob.type).includes('webm') ? 'webm' : 'mp4';
  const file = new File([blob], `popup-${new Date(a.createdAt).toISOString().slice(0, 16).replace(/[:T]/g, '-')}.${ext}`, { type: blob.type || `video/${ext}` });
  if (navigator.canShare?.({ files: [file] })) {
    void navigator.share({ files: [file] }).catch(() => {});
    return;
  }
  const url = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function StillCard({ title, still, cal, tol }: { title: string; still: Still | null; cal: CalibrationRecord; tol: { along: number; across: number } }) {
  const url = useBlobUrl(still?.key);
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  useEffect(() => {
    if (!url) return setCanvas(null);
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      c.getContext('2d')!.drawImage(img, 0, 0);
      setCanvas(c);
    };
    img.src = url;
  }, [url]);
  return (
    <div className="tile" style={{ padding: 8 }}>
      <div className="label" style={{ padding: '2px 4px 6px' }}>{title}</div>
      <div className="video-wrap">
        {canvas ? (
          <Stage
            view={canvas}
            size={() => ({ w: canvas.width, h: canvas.height })}
            draw={(ctx, r, dpr) => drawOverlay(ctx, r, { mapping: cal.mapping, tape: cal.tape, reference: cal.reference, tol, lm: still?.lm ?? null, stance: cal.stance }, dpr)}
          />
        ) : (
          <div className="row" style={{ position: 'absolute', inset: 0, justifyContent: 'center' }}>
            <span className="small faint">{still ? 'Loading…' : 'Not captured'}</span>
          </div>
        )}
      </div>
    </div>
  );
}

const LABELS = Object.keys(TRUTH_TEXT) as TruthLabel[];

function LabelCard({ attempt: a, onChange }: { attempt: AttemptRecord; onChange: (p: Partial<AttemptRecord>) => Promise<void> }) {
  const [note, setNote] = useState(a.note);
  useEffect(() => setNote(a.note), [a.id, a.note]);
  return (
    <div className="card stack">
      <div>
        <h3>What actually happened?</h3>
        <p className="small muted">Labelling attempts lets the validation lab measure how often the app is right.{a.scenario ? ' Pre-filled from the instruction you were given.' : ''}</p>
      </div>
      <div className="row wrap" style={{ gap: 8 }}>
        {LABELS.map((l) => (
          <button key={l} className="chip" aria-pressed={a.label === l} onClick={() => onChange({ label: a.label === l ? null : l })}>
            {TRUTH_TEXT[l]}
          </button>
        ))}
      </div>
      <label className="field">
        <span>Note</span>
        <input className="input" value={note} placeholder="e.g. back foot slid on the carpet" onChange={(e) => setNote(e.target.value)} onBlur={() => note !== a.note && onChange({ note })} />
      </label>
    </div>
  );
}

function ModelCompare({ attempt: a, cal, onUpdate }: { attempt: AttemptRecord; cal: CalibrationRecord; onUpdate: (a: AttemptRecord) => void }) {
  const app = useApp();
  const [busy, setBusy] = useState<ModelVariant | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  if (a.model === 'sim') return null;
  const run = async (m: ModelVariant) => {
    setBusy(m);
    setError(null);
    try {
      onUpdate(await reanalyseAttempt(a, cal, app.settings, m, setProgress));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
      setProgress(0);
    }
  };
  return (
    <div className="card stack">
      <div>
        <h3>Re-analyse every frame</h3>
        <p className="small muted">Runs a pose model over each frame of the clip (slower than live) to compare models on the same attempt.</p>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Analysis</th>
            <th>Verdict</th>
            <th>Pop-up</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Live ({a.model})</td>
            <td><OutcomeBadge outcome={a.analysis.outcome} /></td>
            <td>{fmtSec(a.analysis.timing.popupMs)}</td>
          </tr>
          {(['lite', 'full', 'heavy'] as const).map((m) =>
            a.alt?.[m] ? (
              <tr key={m}>
                <td>Every frame ({m})</td>
                <td><OutcomeBadge outcome={a.alt[m]!.analysis.outcome} /></td>
                <td>{fmtSec(a.alt[m]!.analysis.timing.popupMs)}</td>
              </tr>
            ) : null,
          )}
        </tbody>
      </table>
      {error && <Notice kind="error">{error}</Notice>}
      <div className="row wrap">
        {(['lite', 'full', 'heavy'] as const).map((m) => (
          <button key={m} className="btn small" disabled={!!busy} onClick={() => run(m)}>
            {busy === m ? `${Math.round(progress * 100)}%` : `Run ${m}`}
          </button>
        ))}
      </div>
    </div>
  );
}
